import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  ACCOUNT_PROVIDER_LABELS,
  NICKNAME_MAX,
  validateNickname,
  type AccountProvider,
  type Mbti,
  type ThemeName,
} from '@tarot/shared';
import { deleteAccount, updateMbti, updateNickname, updateTheme } from '../../lib/api';
import { THEME_OPTIONS, applyTheme } from '../../lib/theme';
import { MbtiPicker } from '../../components/MbtiPicker';
import { useMe } from '../auth/useMe';
import { PrimaryLink } from '../../components/Buttons';
import { ReviewCard } from '../reading/ReviewCta';

/** 내 정보 — 닉네임 관리 + 화면 테마(회원 전용) + 회원탈퇴. 로그아웃은 헤더 메뉴에 */
export function ProfilePage() {
  const { me, isLoading } = useMe();

  if (isLoading) return <p className="py-16 text-center text-moon-dim">불러오는 중…</p>;

  if (!me) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
        <h2 className="text-2xl font-bold">내 정보</h2>
        <p className="text-moon-dim">로그인하면 닉네임과 화면 테마를 관리할 수 있어요</p>
        <PrimaryLink to="/login" className="max-w-xs">
          로그인하기
        </PrimaryLink>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-5">
      <h2 className="text-2xl font-bold">내 정보</h2>
      <NicknameCard nickname={me.nickname} provider={me.provider} />
      <MbtiCard current={me.mbti} />
      <ThemeCard current={me.theme} />
      {/* 토스 미니앱에서만 보인다 — 웹에서는 아무것도 렌더하지 않는다 */}
      <ReviewCard />
      <div className="mt-auto pt-4">
        <DeleteAccountSection />
      </div>
    </div>
  );
}

/* ── 닉네임 관리 ── */

// provider 라벨은 shared 단일 출처. 토스(미니앱)까지 포함한 목록을 쓴다
const providerLabel = (p: string) => ACCOUNT_PROVIDER_LABELS[p as AccountProvider] ?? p;

function NicknameCard({ nickname, provider }: { nickname: string; provider: string }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(nickname);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const check = validateNickname(value);
    if (!check.ok) return setError(check.reason);
    setBusy(true);
    setError(null);
    try {
      await updateNickname(check.value);
      await qc.invalidateQueries({ queryKey: ['me'] });
      setEditing(false);
    } catch {
      setError('닉네임을 바꾸지 못했어요');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="flex flex-col gap-3 rounded-3xl border border-white/10 bg-white/5 p-4">
      <h3 className="text-sm font-semibold text-moon-dim">닉네임</h3>
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {editing ? (
            <input
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                setError(null);
              }}
              maxLength={NICKNAME_MAX}
              autoFocus
              className="w-40 rounded-lg border border-gold/50 bg-night-2 px-3 py-1.5 text-sm focus:outline-none"
              onKeyDown={(e) => e.key === 'Enter' && save()}
            />
          ) : (
            <span className="truncate rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 font-semibold">
              {nickname}
            </span>
          )}
          <span className="shrink-0 text-xs text-moon-dim">{providerLabel(provider)} 로그인</span>
        </div>
        {editing ? (
          <div className="flex shrink-0 gap-1.5">
            <button
              onClick={save}
              disabled={busy}
              className="rounded-lg bg-gold px-3 py-1.5 text-sm font-semibold text-night disabled:opacity-50"
            >
              저장
            </button>
            <button
              onClick={() => {
                setEditing(false);
                setValue(nickname);
                setError(null);
              }}
              className="rounded-lg border border-white/15 px-3 py-1.5 text-sm text-moon-dim"
            >
              취소
            </button>
          </div>
        ) : (
          <button
            onClick={() => setEditing(true)}
            className="shrink-0 rounded-lg border border-white/15 px-3 py-1.5 text-sm text-moon-dim transition hover:border-gold/50 hover:text-moon"
          >
            닉네임 수정
          </button>
        )}
      </div>
      <p className="text-xs text-moon-dim/70">커플·우정 방에서 함께 보는 사람들에게 보여요</p>
      {error && <p className="text-sm text-red-300">⚠️ {error}</p>}
    </section>
  );
}

/* ── MBTI (선택 입력, DB 저장 — 저장해두면 타로 볼 때 자동 적용) ── */

function MbtiCard({ current }: { current: Mbti | '' }) {
  const qc = useQueryClient();
  const [mbti, setMbti] = useState<Mbti | null>(current || null);
  const [error, setError] = useState(false);

  const select = async (v: Mbti | null) => {
    const prev = mbti;
    setMbti(v);
    setError(false);
    try {
      await updateMbti(v ?? '');
      await qc.invalidateQueries({ queryKey: ['me'] });
    } catch {
      setMbti(prev);
      setError(true);
    }
  };

  return (
    <section className="flex flex-col gap-3 rounded-3xl border border-white/10 bg-white/5 p-4">
      <h3 className="text-sm font-semibold text-moon-dim">
        MBTI <span className="font-normal text-moon-dim/70">(선택)</span>
      </h3>
      <MbtiPicker value={mbti} onChange={select} />
      <p className="text-xs text-moon-dim/70">
        저장해두면 타로 볼 때 자동으로 적용돼, 성향에 맞는 말투와 조언으로 해석해 드려요. 해석 결과
        자체가 바뀌지는 않아요.
      </p>
      {error && <p className="text-sm text-red-300">⚠️ MBTI를 저장하지 못했어요</p>}
    </section>
  );
}

/* ── 화면 테마 (회원 전용, DB 저장 — 기기·브라우저 바뀌어도 유지) ── */

function ThemeCard({ current }: { current: ThemeName }) {
  const qc = useQueryClient();
  const [theme, setThemeState] = useState<ThemeName>(current);
  const [error, setError] = useState(false);

  const select = async (t: ThemeName) => {
    const prev = theme;
    setThemeState(t);
    applyTheme(t); // 화면 즉시 반영
    setError(false);
    try {
      await updateTheme(t); // DB 저장
      await qc.invalidateQueries({ queryKey: ['me'] });
    } catch {
      setThemeState(prev); // 저장 실패 시 되돌림
      applyTheme(prev);
      setError(true);
    }
  };

  return (
    <section className="flex flex-col gap-3 rounded-3xl border border-white/10 bg-white/5 p-4">
      <h3 className="text-sm font-semibold text-moon-dim">화면 테마</h3>
      <div className="grid grid-cols-3 gap-1.5 rounded-2xl bg-night-2 p-1.5">
        {THEME_OPTIONS.map((o) => (
          <button
            key={o.value}
            onClick={() => select(o.value)}
            aria-pressed={theme === o.value}
            className={`rounded-xl border py-2 text-sm font-medium transition ${
              theme === o.value
                ? 'border-gold bg-gold font-semibold text-night'
                : 'border-white/15 bg-night/60 text-moon-dim hover:border-gold/40 hover:text-moon'
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
      <p className="text-xs text-moon-dim/70">
        계정에 저장돼 다른 기기에서 로그인해도 유지돼요 · 시스템은 기기 설정을 따라가요
      </p>
      {error && <p className="text-sm text-red-300">⚠️ 테마를 저장하지 못했어요</p>}
    </section>
  );
}

/* ── 회원탈퇴 — 하단 버튼 + 인라인 확인 후 즉시 파기 ── */

function DeleteAccountSection() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const onDelete = async () => {
    setBusy(true);
    try {
      await deleteAccount();
      await qc.invalidateQueries({ queryKey: ['me'] });
      await qc.invalidateQueries({ queryKey: ['readings'] });
      navigate('/');
    } catch {
      setBusy(false);
      setConfirming(false);
    }
  };

  if (!confirming) {
    return (
      <button
        onClick={() => setConfirming(true)}
        className="w-full rounded-2xl border border-red-400/30 py-3 text-sm font-medium text-red-300/80 transition hover:border-red-400/60 hover:bg-red-500/10 hover:text-red-300"
      >
        회원탈퇴
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-red-400/40 bg-red-500/10 p-4">
      <p className="text-sm leading-relaxed text-red-300">
        탈퇴하면 식별키·닉네임·저장된 타로가 <b>즉시 삭제</b>되고 복구할 수 없어요.
      </p>
      <div className="flex gap-2">
        <button
          onClick={onDelete}
          disabled={busy}
          className="flex-1 rounded-xl bg-red-500/80 py-2.5 text-sm font-semibold text-[#ffffff] disabled:opacity-50"
        >
          {busy ? '삭제 중…' : '탈퇴하기'}
        </button>
        <button
          onClick={() => setConfirming(false)}
          disabled={busy}
          className="flex-1 rounded-xl border border-white/15 py-2.5 text-sm text-moon-dim"
        >
          취소
        </button>
      </div>
    </div>
  );
}
